import React, { useState } from 'react';
import { ArrowLeftIcon, ScanIcon, CheckIcon, AlertCircleIcon, PackageIcon, ScaleIcon, QrCode, XIcon } from 'lucide-react';
interface Ingredient {
  id: string;
  name: string;
  amount: number;
  unit: string;
  tolerance: number;
  requiresWeighing: boolean;
  packageSize?: string;
}
interface WeighingProcessProps {
  ingredient: Ingredient;
  onComplete: (batch: string) => void;
  onCancel: () => void;
}
type WeighingStep = 'ingredient-scan' | 'scale-select' | 'scale-confirm' | 'weighing';
export const WeighingProcess = ({
  ingredient,
  onComplete,
  onCancel
}: WeighingProcessProps) => {
  const [currentStep, setCurrentStep] = useState<WeighingStep>('scale-select');
  const [scannedIngredientBatch] = useState('RW-22445'); // Changed from '116425'
  const [selectedScale, setSelectedScale] = useState<string | null>(null);
  const [scannedWeight, setScannedWeight] = useState<number | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Simuliere gespeicherte Waage
  const [savedScale] = useState(() => {
    const scale = localStorage.getItem('savedScale');
    if (scale) {
      setSelectedScale(scale);
      setCurrentStep('weighing');
    }
    return scale;
  });
  const handleSelectScale = () => {
    setIsScanning(true);
    setTimeout(() => {
      const scale = 'Waage-K1 (Kutter 1)';
      setSelectedScale(scale);
      localStorage.setItem('savedScale', scale);
      setIsScanning(false);
      setCurrentStep('weighing');
      // Direkt ein Gewicht simulieren
      simulateWeight();
    }, 1000);
  };
  // Neue Funktion für die Gewichtssimulation
  const simulateWeight = () => {
    const randomFactor = Math.random();
    let weight;
    if (randomFactor < 0.7) {
      const minWeight = ingredient.amount - ingredient.tolerance;
      const maxWeight = ingredient.amount + ingredient.tolerance;
      weight = minWeight + Math.random() * (maxWeight - minWeight);
    } else {
      const belowMin = ingredient.amount - ingredient.tolerance - Math.random() * 5;
      const aboveMax = ingredient.amount + ingredient.tolerance + Math.random() * 5;
      weight = Math.random() < 0.5 ? belowMin : aboveMax;
    }
    setScannedWeight(parseFloat(weight.toFixed(2)));
  };
  const handleStartWeighing = () => {
    setCurrentStep('weighing');
    const randomFactor = Math.random();
    let weight;
    if (randomFactor < 0.7) {
      const minWeight = ingredient.amount - ingredient.tolerance;
      const maxWeight = ingredient.amount + ingredient.tolerance;
      weight = minWeight + Math.random() * (maxWeight - minWeight);
    } else {
      const belowMin = ingredient.amount - ingredient.tolerance - Math.random() * 5;
      const aboveMax = ingredient.amount + ingredient.tolerance + Math.random() * 5;
      weight = Math.random() < 0.5 ? belowMin : aboveMax;
    }
    setScannedWeight(parseFloat(weight.toFixed(2)));
  };
  const handleConfirmWeight = () => {
    if (!scannedWeight) return;
    const minAllowed = ingredient.amount - ingredient.tolerance;
    const maxAllowed = ingredient.amount + ingredient.tolerance;
    if (scannedWeight < minAllowed || scannedWeight > maxAllowed) {
      setError(`Gewicht außerhalb der Toleranz! (${minAllowed.toFixed(2)} - ${maxAllowed.toFixed(2)} ${ingredient.unit})`);
      return;
    }
    onComplete(scannedIngredientBatch); // Übergebe die Chargenummer
  };
  const isWeightWithinTolerance = (weight: number) => {
    const minAllowed = ingredient.amount - ingredient.tolerance;
    const maxAllowed = ingredient.amount + ingredient.tolerance;
    return weight >= minAllowed && weight <= maxAllowed;
  };
  return <div className="flex flex-col h-full" data-id="element-248">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm" data-id="element-249">
        {/* Ingredient Name Header */}
        <div className="border-b py-2 px-3" data-id="element-250">
          <div className="flex items-baseline gap-2" data-id="element-251">
            <h3 className="text-base font-medium" data-id="element-252">{ingredient.name}</h3>
            <span className="text-xs text-gray-400" data-id="element-253">Art.Nr.: 45678</span>
          </div>
          <div className="text-xs text-gray-500" data-id="element-254">Lieferant: ACME GmbH</div>
          {/* Key Information Grid */}
          <div className="grid grid-cols-2 gap-2 mt-2" data-id="element-255">
            {/* Target Weight */}
            <div className="bg-gray-50 rounded p-2" data-id="element-256">
              <div className="text-[10px] text-gray-500" data-id="element-257">Zielgewicht</div>
              <div className="text-lg font-bold text-[#e76405]" data-id="element-258">
                {ingredient.amount}
                <span className="text-xs ml-1" data-id="element-259">{ingredient.unit}</span>
              </div>
              <div className="text-xs text-gray-600" data-id="element-260">
                ± {ingredient.tolerance} {ingredient.unit}
              </div>
            </div>
            {/* Batch Number */}
            <div className="bg-gray-50 rounded p-2" data-id="element-261">
              <div data-id="element-262">
                <div className="text-[10px] text-gray-500" data-id="element-263">Charge</div>
                <div className="text-base font-bold" data-id="element-264">
                  {scannedIngredientBatch}
                </div>
              </div>
              <div className="mt-0.5" data-id="element-265">
                <div className="text-[10px] text-gray-500" data-id="element-266">MHD</div>
                <div className="text-xs font-medium text-gray-700" data-id="element-267">
                  31.12.24
                </div>
              </div>
            </div>
          </div>
          {/* Selected Scale - if available */}
          {selectedScale && <div className="mt-2" data-id="element-268">
              <div className="flex items-center justify-between bg-[#e76405] bg-opacity-10 text-[#e76405] px-2 py-1.5 rounded" data-id="element-269">
                <div className="flex items-center gap-1.5" data-id="element-270">
                  <ScaleIcon size={16} data-id="element-271" />
                  <span className="text-sm font-medium" data-id="element-272">{selectedScale}</span>
                </div>
                <button onClick={() => {
              localStorage.removeItem('savedScale');
              setSelectedScale(null);
              setCurrentStep('scale-select');
              setScannedWeight(null);
              setError(null);
            }} className="hover:bg-[#e76405] hover:bg-opacity-20 rounded p-1" data-id="element-273">
                  <XIcon size={14} data-id="element-274" />
                </button>
              </div>
            </div>}
        </div>
        {/* Main Content */}
        <div className="p-3" data-id="element-275">
          {currentStep === 'scale-select' && <div className="flex flex-col items-center py-6" data-id="element-276">
              <QrCode size={100} className="text-[#e76405] mb-4" data-id="element-277" />
              <h2 className="text-lg font-medium mb-1" data-id="element-278">Waage verbinden</h2>
              <p className="text-sm text-gray-500 mb-4" data-id="element-279">
                QR-Code der Waage scannen
              </p>
              {isScanning && <div className="flex items-center justify-center gap-1.5 text-[#e76405]" data-id="element-280">
                  <ScanIcon className="animate-pulse" size={18} data-id="element-281" />
                  <span className="text-sm" data-id="element-282">Scanne...</span>
                </div>}
              {/* Demo Button */}
              <div className="fixed bottom-2 right-2 opacity-30 hover:opacity-100 transition-opacity" data-id="element-283">
                <button onClick={handleSelectScale} className="bg-gray-200 text-gray-600 py-1 px-2 rounded text-[10px] hover:bg-gray-300" data-id="element-284">
                  <span className="flex items-center gap-1" data-id="element-285">
                    <ScanIcon size={10} data-id="element-286" />
                    Demo
                  </span>
                </button>
              </div>
            </div>}
          {currentStep === 'weighing' && <div className="flex flex-col" data-id="element-287">
              <div className={`p-3 rounded mb-3 ${scannedWeight ? isWeightWithinTolerance(scannedWeight) ? 'bg-green-50/70' : 'bg-red-50/70' : 'bg-gray-50'}`} data-id="element-288">
                <div className="text-center" data-id="element-289">
                  <div className="text-xs text-gray-500 mb-1" data-id="element-290">
                    Aktuelles Gewicht
                  </div>
                  <div className={`text-2xl font-bold ${scannedWeight !== null ? isWeightWithinTolerance(scannedWeight) ? 'text-green-600' : 'text-red-600' : 'text-[#e76405]'}`} data-id="element-291">
                    {scannedWeight !== null ? scannedWeight.toFixed(2) : '0.00'}{' '}
                    <span className="text-lg" data-id="element-292">{ingredient.unit}</span>
                  </div>
                </div>
              </div>
              {error && <div className="flex items-center gap-1.5 p-2 bg-red-50 text-red-700 rounded text-xs mb-3" data-id="element-293">
                  <AlertCircleIcon size={16} data-id="element-294" />
                  <span data-id="element-295">{error}</span>
                </div>}
              <button onClick={handleConfirmWeight} disabled={scannedWeight && !isWeightWithinTolerance(scannedWeight)} className={`flex items-center justify-center gap-2 py-2.5 px-4 rounded text-sm ${scannedWeight && !isWeightWithinTolerance(scannedWeight) ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-[#e76405] text-white'}`} data-id="element-296">
                <CheckIcon size={18} data-id="element-297" />
                Bestätigen
              </button>
            </div>}
        </div>
      </div>
    </div>;
};